import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Save, RefreshCw, TestTube, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../utils/apiConfig';
import { toast } from '../../hooks/use-toast';

const PricingConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/pricing/config`);
      console.log('Pricing config loaded:', response.data);
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading pricing config:', error);
      toast({
        title: 'Error loading configuration',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      
      // Get admin token
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await axios.put(
        `${API_URL}/pricing/config`,
        config,
        {
          params: { admin_user_id: user.id },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Configuration saved',
        description: `Updated to version ${response.data.version}`,
      });

      // Reload to get latest version
      await loadConfig();
    } catch (error) {
      toast({
        title: 'Error saving configuration',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const runTests = async () => {
    try {
      setTesting(true);
      const response = await axios.get(`${API_URL}/pricing/test-scenarios`);
      setTestResults(response.data);
      
      toast({
        title: 'Test scenarios completed',
        description: `Ran ${response.data.scenarios.length} test scenarios`,
      });
    } catch (error) {
      toast({
        title: 'Error running tests',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  const updateNestedValue = (path, value) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = parseFloat(value) || 0;
    setConfig(newConfig);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/admin')} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Pricing Configuration</h1>
            <p className="text-sm text-gray-600">
              Version {config?.version} • Last updated: {new Date(config?.updated_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={runTests} disabled={testing} variant="outline">
            <TestTube className="w-4 h-4 mr-2" />
            {testing ? 'Running...' : 'Run Tests'}
          </Button>
          <Button onClick={saveConfig} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Core Settings */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Core Settings</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label>Fuel Price Baseline (MWK/L)</Label>
            <Input
              type="number"
              value={config?.fuel_price_baseline ?? ''}
              onChange={(e) => updateNestedValue('fuel_price_baseline', e.target.value)}
              className="mt-2"
            />
          </div>
          <div>
            <Label>Minimum Distance (km)</Label>
            <Input
              type="number"
              step="0.1"
              value={config?.minimum_distance ?? ''}
              onChange={(e) => updateNestedValue('minimum_distance', e.target.value)}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">Distance included in minimum fare</p>
          </div>
          <div>
            <Label>Free Waiting Time (minutes)</Label>
            <Input
              type="number"
              step="0.5"
              value={config?.free_waiting_minutes ?? ''}
              onChange={(e) => updateNestedValue('free_waiting_minutes', e.target.value)}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">Minutes before charging waiting fees</p>
          </div>
          <div>
            <Label>Platform Commission (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={config?.platform_commission != null ? (config.platform_commission * 100) : ''}
              onChange={(e) => updateNestedValue('platform_commission', parseFloat(e.target.value) / 100)}
              className="mt-2"
            />
          </div>
        </div>
      </Card>

      {/* Vehicle Type Pricing */}
      {['economy', 'premium', 'bike'].map((vehicleType) => (
        <Card key={vehicleType} className="p-6">
          <h2 className="text-lg font-semibold mb-4 capitalize">{vehicleType} Vehicle Pricing</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label>Minimum Fare (MWK)</Label>
              <Input
                type="number"
                value={config?.[vehicleType]?.min_fare ?? ''}
                onChange={(e) => updateNestedValue(`${vehicleType}.min_fare`, e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Per-KM Rate (MWK/km)</Label>
              <Input
                type="number"
                value={config?.[vehicleType]?.per_km_rate ?? ''}
                onChange={(e) => updateNestedValue(`${vehicleType}.per_km_rate`, e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">After {config?.minimum_distance}km</p>
            </div>
            <div>
              <Label>Waiting Rate (MWK/min)</Label>
              <Input
                type="number"
                value={config?.[vehicleType]?.waiting_rate_per_minute ?? ''}
                onChange={(e) => updateNestedValue(`${vehicleType}.waiting_rate_per_minute`, e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">After {config?.free_waiting_minutes} min</p>
            </div>
            <div>
              <Label>Min Driver Payout (MWK)</Label>
              <Input
                type="number"
                value={config?.[vehicleType]?.min_driver_payout ?? ''}
                onChange={(e) => updateNestedValue(`${vehicleType}.min_driver_payout`, e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Cancel Fee After Arrival (MWK)</Label>
              <Input
                type="number"
                value={config?.[vehicleType]?.cancel_fee_after_arrival ?? ''}
                onChange={(e) => updateNestedValue(`${vehicleType}.cancel_fee_after_arrival`, e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">100% to driver</p>
            </div>
          </div>
        </Card>
      ))}

      {/* Distance Tapering */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Distance Tapering</h2>
        <p className="text-sm text-gray-600 mb-4">
          Apply discounts to per-km rates for longer trips (protects riders on long distances)
        </p>
        
        {/* Tier Boundaries */}
        <div className="mb-4 pb-4 border-b">
          <h3 className="text-sm font-semibold mb-3 text-gray-700">Tier Boundaries (km)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Tier 1 Max Distance (km)</Label>
              <Input
                type="number"
                step="0.5"
                value={config?.distance_tapering?.tier_1_max ?? ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_1_max', e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">0 - {config?.distance_tapering?.tier_1_max}km</p>
            </div>
            <div>
              <Label>Tier 2 Max Distance (km)</Label>
              <Input
                type="number"
                step="0.5"
                value={config?.distance_tapering?.tier_2_max ?? ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_2_max', e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">{config?.distance_tapering?.tier_1_max} - {config?.distance_tapering?.tier_2_max}km</p>
            </div>
            <div>
              <Label>Tier 3 Max Distance (km)</Label>
              <Input
                type="number"
                step="0.5"
                value={config?.distance_tapering?.tier_3_max ?? ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_3_max', e.target.value)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">{config?.distance_tapering?.tier_2_max} - {config?.distance_tapering?.tier_3_max}km</p>
            </div>
          </div>
        </div>
        
        {/* Tier Multipliers */}
        <div>
          <h3 className="text-sm font-semibold mb-3 text-gray-700">Rate Multipliers (%)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Tier 1: 0-{config?.distance_tapering?.tier_1_max}km</Label>
              <Input
                type="number"
                step="0.01"
                value={config?.distance_tapering?.tier_1_multiplier != null ? (config.distance_tapering.tier_1_multiplier * 100) : ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_1_multiplier', parseFloat(e.target.value) / 100)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">% of base rate</p>
            </div>
            <div>
              <Label>Tier 2: {config?.distance_tapering?.tier_1_max}-{config?.distance_tapering?.tier_2_max}km</Label>
              <Input
                type="number"
                step="0.01"
                value={config?.distance_tapering?.tier_2_multiplier != null ? (config.distance_tapering.tier_2_multiplier * 100) : ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_2_multiplier', parseFloat(e.target.value) / 100)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">% of base rate</p>
            </div>
            <div>
              <Label>Tier 3: {config?.distance_tapering?.tier_2_max}-{config?.distance_tapering?.tier_3_max}km</Label>
              <Input
                type="number"
                step="0.01"
                value={config?.distance_tapering?.tier_3_multiplier != null ? (config.distance_tapering.tier_3_multiplier * 100) : ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_3_multiplier', parseFloat(e.target.value) / 100)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">% of base rate</p>
            </div>
            <div>
              <Label>Tier 4: {config?.distance_tapering?.tier_3_max}km+</Label>
              <Input
                type="number"
                step="0.01"
                value={config?.distance_tapering?.tier_4_multiplier != null ? (config.distance_tapering.tier_4_multiplier * 100) : ''}
                onChange={(e) => updateNestedValue('distance_tapering.tier_4_multiplier', parseFloat(e.target.value) / 100)}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">% of base rate</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Surge Pricing */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Surge Pricing</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Surge Enabled</Label>
            <select
              value={config?.surge?.enabled ? 'true' : 'false'}
              onChange={(e) => updateNestedValue('surge.enabled', e.target.value === 'true')}
              className="mt-2 w-full border rounded px-3 py-2"
            >
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
          <div>
            <Label>Minimum Multiplier</Label>
            <Input
              type="number"
              step="0.1"
              value={config?.surge?.min_multiplier ?? ''}
              onChange={(e) => updateNestedValue('surge.min_multiplier', e.target.value)}
              className="mt-2"
            />
          </div>
          <div>
            <Label>Maximum Multiplier</Label>
            <Input
              type="number"
              step="0.1"
              value={config?.surge?.max_multiplier ?? ''}
              onChange={(e) => updateNestedValue('surge.max_multiplier', e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
      </Card>

      {/* Driver Protection */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Driver Protection</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Long Pickup Bonus (MWK)</Label>
            <Input
              type="number"
              value={config?.long_pickup_bonus ?? ''}
              onChange={(e) => updateNestedValue('long_pickup_bonus', e.target.value)}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">Bonus when pickup &gt; {config?.long_pickup_threshold}km away</p>
          </div>
          <div>
            <Label>Long Pickup Threshold (km)</Label>
            <Input
              type="number"
              step="0.1"
              value={config?.long_pickup_threshold ?? ''}
              onChange={(e) => updateNestedValue('long_pickup_threshold', e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
      </Card>

      {/* Test Results */}
      {testResults && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Test Results</h2>
          <div className="space-y-4">
            {testResults.scenarios.map((scenario, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{scenario.name}</h3>
                    <p className="text-sm text-gray-600">{scenario.description}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-gray-600">Total Fare</p>
                    <p className="font-bold">MWK {scenario.result.total_fare?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Driver Payout</p>
                    <p className="font-bold text-green-600">MWK {scenario.result.driver_gross_payout?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Platform Fee</p>
                    <p className="font-bold text-blue-600">MWK {scenario.result.platform_fee?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Surge</p>
                    <p className="font-bold">{scenario.result.surge_multiplier}x</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default PricingConfig;
